import { roundToTwoDecimals } from '@fruitful/fruitils';
import { type TaxFiling } from '@fruitful/types';

import { EffectiveTaxRateCalculator } from '../taxRate/EffectiveTaxRateCalculator';
import { requiredMinimumDistributionPeriodsByAge } from './assumptions/requiredMinimumDistributions';
import { SocialSecurityBenefitCalculator } from './SocialSecurityBenefitCalculator';
import {
    DistributionType,
    type GrossRetirementDistribution,
    type MemberInput,
    type RetirementAccount,
    RetirementAccountTaxTreatment,
    retirementAccountTaxTreatment,
    type RetirementAssets,
    type RetirementDistribution,
    type RetirementDistributionResult,
} from './types';

// For a given year and set of retirement accounts, calculates the tax-aware drawdown distribution across the accounts.
// TODO - Handle joint memberships, especially with respect to mixes of tax filing statuses.
export class RetirementDistributionCalculator {
    private readonly LONG_TERM_CAPITAL_GAINS_RATE = 0.15; // 15% for taxable accounts.
    private readonly RMD_STARTING_AGE = 75; // Age when Required Minimum Distributions begin. TODO - This is different for members born before 1960.

    constructor(
        private readonly retirementExpenses: number,
        private readonly retirementAssets: RetirementAssets,
        private readonly memberInputs: MemberInput[],
        private readonly year: number,
        private readonly taxFilingStatus: TaxFiling,
        private readonly residentialLocation: string,
        private readonly totalGrossSocialSecurity: number,
    ) {}

    public calculate(): RetirementDistributionResult {
        // Step 1: Calculate gross required minimum distributions (RMDs) and social security
        const grossRMDs = this.calculateGrossRequiredMinimumDistributions();
        const totalGrossSocialSecurity = this.totalGrossSocialSecurity;

        // Step 2: Use a binary search to calculate optimal elective distribution amount.
        // Since tax rate, social security, and distributions affect one another,
        // a binary search allows us to iterate until we find the desired amount.
        const tolerance = 100; // Allow excess withdrawal up to this amount.
        let low = 0;
        const MAX_MARGINAL_TAX_RATE = 0.5;
        let high = this.retirementExpenses / (1 - MAX_MARGINAL_TAX_RATE); // Seems like a safe upperbound? Can increase if needed.
        let bestResult: RetirementDistributionResult | null = null;

        // Loop until the bounds for high, low are close enough. Also ensure a best result is found.
        while (high - low > tolerance || bestResult == null) {
            const mid = (low + high) / 2;
            const totalGrossElectiveDistributionGuess = mid; // The current guess in the binary search is the midpoint.

            // Given a total amount of gross distributions to make, determine how the distributions are spread across accounts, taking RMDs into consideration.
            const grossElectiveDistributions =
                this.calculateGrossElectiveDistributions(
                    totalGrossElectiveDistributionGuess,
                    grossRMDs,
                );

            // Calculate the result from the distribution calculation.
            const distributionResult = this.calculateDistributionResult(
                grossRMDs,
                grossElectiveDistributions,
                totalGrossSocialSecurity,
            );

            if (distributionResult.targetMet) {
                // Success case, but total inflows may be too large.
                high = mid;
                bestResult = distributionResult;
            } else {
                // Fail case (not enough to cover expenses). Need to increase total non rmd distribution amount.
                low = mid;

                // Update the best result even though it failed so that we can return a failure case state.
                if (!bestResult?.targetMet) {
                    bestResult = distributionResult;
                }
            }
        }

        return bestResult;
    }

    /**
     * Calculates the effective tax rate, distributions, and total amount of net inflows for a year which includes: social security, RMD, elective distributions.
     * @private
     */
    private calculateDistributionResult(
        grossRmds: GrossRetirementDistribution[],
        grossElectiveDistributions: GrossRetirementDistribution[],
        totalGrossSocialSecurity: number,
    ): RetirementDistributionResult {
        const grossDistributions = [
            ...grossRmds,
            ...grossElectiveDistributions,
        ];

        // Calculate total taxable amount across all Distributions
        const totalTaxableDistributions = roundToTwoDecimals(
            grossDistributions.reduce(
                (sum, dist) => sum + dist.taxableAmount,
                0,
            ),
        );

        // Calculate taxable amount of social security.
        const taxableSocialSecurity =
            SocialSecurityBenefitCalculator.calculateTaxableSocialSecurity(
                totalGrossSocialSecurity,
                totalTaxableDistributions,
                this.taxFilingStatus,
            );

        // Calculate total taxable amount for Distributions that are taxed as ordinary income.
        // Assumes that taxable accounts are always Long-Term Capital Gains, and so they are excluded from this calculation.
        // ROTH: Never taxable
        // NON_ROTH: Always taxed as ordinary income
        // TAXABLE: Taxed at LTCG rate.
        const totalOrdinaryIncomeTaxableDistributions = grossDistributions
            .filter(
                (dist) =>
                    retirementAccountTaxTreatment[dist.account.type] ===
                    RetirementAccountTaxTreatment.NON_ROTH,
            )
            .reduce((sum, dist) => sum + dist.taxableAmount, 0);

        // Calculate the effective tax rate for taxable ordinary income.
        const ordinaryIncomeTaxCalculator = new EffectiveTaxRateCalculator(
            taxableSocialSecurity,
            totalOrdinaryIncomeTaxableDistributions,
            this.residentialLocation,
            this.taxFilingStatus,
        );
        const ordinaryIncomeEffectiveTaxRate =
            ordinaryIncomeTaxCalculator.calculate();

        // Calculate net social security.
        const totalSocialSecurityTax = roundToTwoDecimals(
            taxableSocialSecurity * ordinaryIncomeEffectiveTaxRate,
        );
        const netSocialSecurity = roundToTwoDecimals(
            totalGrossSocialSecurity - totalSocialSecurityTax,
        );

        // Create full (net) distributions from gross distributions and tax rate.
        const distributions = grossDistributions.map((grossDistribution) => {
            return this.createDistribution(
                grossDistribution,
                ordinaryIncomeEffectiveTaxRate,
            );
        });

        // Calculate net distributions.
        const netDistributions = roundToTwoDecimals(
            distributions.reduce(
                (sum, dist) => sum + dist.netDistributionAmount,
                0,
            ),
        );

        // Calculate total net inflows.
        const totalNetInflows = roundToTwoDecimals(
            netSocialSecurity + netDistributions,
        );

        // Calculate _blended_ effective tax rate across ordinary income, and long-term capital gains.
        const distributionsTaxLiability = distributions.reduce(
            (sum, dist) => sum + dist.taxAmount,
            0,
        );
        const totalTaxLiability =
            distributionsTaxLiability + totalSocialSecurityTax;
        const agi = totalTaxableDistributions + taxableSocialSecurity;
        const effectiveTaxRate = agi <= 0 ? 0 : totalTaxLiability / agi;

        const targetMet = totalNetInflows >= this.retirementExpenses;

        return {
            totalNetInflows,
            effectiveTaxRate,
            distributions,
            totalSocialSecurityTax,
            targetMet,
        };
    }

    private createDistribution(
        grossDistribution: GrossRetirementDistribution,
        ordinaryIncomeEffectiveTaxRate: number,
    ): RetirementDistribution {
        const taxRate = this.getEffectiveDistributionTaxRateForAccount(
            grossDistribution.account,
            ordinaryIncomeEffectiveTaxRate,
        );

        const taxAmount = roundToTwoDecimals(
            grossDistribution.taxableAmount * taxRate,
        );
        const netDistributionAmount =
            grossDistribution.grossDistributionAmount - taxAmount;

        return {
            ...grossDistribution,
            taxAmount,
            netDistributionAmount,
        };
    }

    private getEffectiveDistributionTaxRateForAccount(
        account: RetirementAccount,
        effectiveTaxRate: number,
    ): number {
        const taxTreatment = retirementAccountTaxTreatment[account.type];

        switch (taxTreatment) {
            case RetirementAccountTaxTreatment.ROTH:
                return 0; // 0% tax rate (tax-free)

            case RetirementAccountTaxTreatment.NON_ROTH:
                return effectiveTaxRate; // Use calculated effective tax rate

            case RetirementAccountTaxTreatment.TAXABLE:
                // TODO - Handle different long-term capital gains rates based on ordinary income.
                // TODO - Handle standard deduction for taxable accounts (currently only applied to income taxed as ordinary income).
                return this.LONG_TERM_CAPITAL_GAINS_RATE; // Use 15% capital gains rate

            default:
                throw new Error('Could not determine tax rate.');
        }
    }

    private calculateGrossRequiredMinimumDistributions(): GrossRetirementDistribution[] {
        return this.retirementAssets
            .map((account) => {
                return this.getRequiredMinimumDistribution(account);
            })
            .filter(
                ({ grossDistributionAmount }) => grossDistributionAmount > 0,
            );
    }

    private calculateGrossElectiveDistributions(
        totalGrossAmount: number,
        existingRmds: GrossRetirementDistribution[],
    ): GrossRetirementDistribution[] {
        let remainingGrossNeeded = totalGrossAmount;
        const additionalDistributions: GrossRetirementDistribution[] = [];

        // Distribute across accounts in priority order (taxable → traditional → roth)
        [...this.retirementAssets]
            .sort((a, b) => this.accountSorter(a, b))
            .forEach((account: RetirementAccount) => {
                if (remainingGrossNeeded <= 0) return;

                // Calculate available balance after RMDs
                const existingRmd = existingRmds.find(
                    (rmd) => rmd.account.id === account.id,
                );
                const rmdAmount = existingRmd?.grossDistributionAmount ?? 0;
                const availableBalance = roundToTwoDecimals(
                    account.balance - rmdAmount,
                );

                if (availableBalance <= 0) return;

                // Can't withdraw more than available balance
                const additionalGrossAmount = roundToTwoDecimals(
                    Math.min(remainingGrossNeeded, availableBalance),
                );

                if (additionalGrossAmount > 0) {
                    // Create a distribution.
                    const distribution: GrossRetirementDistribution = {
                        account,
                        grossDistributionAmount: additionalGrossAmount,
                        taxableAmount: this.calculateTaxableAmount(
                            account,
                            additionalGrossAmount,
                        ),
                        type: DistributionType.DRAWDOWN,
                    };

                    additionalDistributions.push(distribution);

                    remainingGrossNeeded = roundToTwoDecimals(
                        remainingGrossNeeded -
                            distribution.grossDistributionAmount,
                    );
                }
            });

        return additionalDistributions;
    }

    private calculateTaxableAmount(
        account: RetirementAccount,
        distributionAmount: number,
    ): number {
        const taxTreatment = retirementAccountTaxTreatment[account.type];

        switch (taxTreatment) {
            case RetirementAccountTaxTreatment.ROTH:
                // Roth accounts: tax-free distributions
                // Assumes all distributions are Qualified (meets the Age and 5-year rules)
                return 0;

            case RetirementAccountTaxTreatment.NON_ROTH:
                // Traditional accounts: fully taxable
                // Assumes all contributions to a Non-Roth account are pre-tax contributions. We do not account for after-tax contributions (basis) which occurs for non-deductible contributions to Non-Roth accounts.
                return distributionAmount;

            case RetirementAccountTaxTreatment.TAXABLE:
                // Taxable brokerage: only tax the gains portion, not cost basis
                return this.calculateTaxableGains(account, distributionAmount);

            default:
                throw new Error('Could not determine Taxable Amount');
        }
    }

    /**
     * Calculates the taxable gains portion of a distribution from a taxable account.
     * Only the gains (appreciation above cost basis) are subject to capital gains tax.
     */
    private calculateTaxableGains(
        account: RetirementAccount,
        distributionAmount: number,
    ): number {
        // If cost basis is zero or negative, assume conservative approach (tax everything)
        if (account.costBasis <= 0) {
            return distributionAmount;
        }

        // Ensure cost basis doesn't exceed current balance (defensive programming)
        const effectiveCostBasis = Math.min(account.costBasis, account.balance);

        // If cost basis equals or exceeds balance, no gains to tax
        if (effectiveCostBasis >= account.balance) {
            return 0;
        }

        // Calculate what percentage of the account represents gains
        const gainsAmount = account.balance - effectiveCostBasis;
        const gainsPercentage = gainsAmount / account.balance;

        // Apply gains percentage to the distribution to get taxable portion
        const taxableGainsAmount = roundToTwoDecimals(
            distributionAmount * gainsPercentage,
        );

        return taxableGainsAmount;
    }

    private getCurrentMemberAge(memberId: string) {
        const member = this.memberInputs.find(
            (member) => member.id === memberId,
        );

        if (!member) {
            throw new Error(`Member ${memberId} not found`);
        }

        return member.currentAge + this.year;
    }

    private getRequiredMinimumDistribution(
        account: RetirementAccount,
    ): GrossRetirementDistribution {
        // Only Non-Roth accounts have RMDs.
        if (
            retirementAccountTaxTreatment[account.type] !==
            RetirementAccountTaxTreatment.NON_ROTH
        ) {
            const grossDistributionAmount = 0;

            return {
                account,
                grossDistributionAmount,
                taxableAmount: this.calculateTaxableAmount(
                    account,
                    grossDistributionAmount,
                ),
                type: DistributionType.RMD,
            };
        }

        const memberAge = this.getCurrentMemberAge(account.owner);

        if (memberAge < this.RMD_STARTING_AGE) {
            const grossDistributionAmount = 0;

            return {
                account,
                grossDistributionAmount,
                taxableAmount: this.calculateTaxableAmount(
                    account,
                    grossDistributionAmount,
                ),
                type: DistributionType.RMD,
            };
        }

        const grossDistributionAmount = roundToTwoDecimals(
            account.balance *
                (1 /
                    (requiredMinimumDistributionPeriodsByAge[
                        memberAge
                    ] as number)),
        );

        return {
            account,
            grossDistributionAmount,
            taxableAmount: this.calculateTaxableAmount(
                account,
                grossDistributionAmount,
            ),
            type: DistributionType.RMD,
        };
    }

    private accountSorter(a: RetirementAccount, b: RetirementAccount) {
        // Helper to assign a sort order based on account type
        function getTaxTreatmentOrder(account: RetirementAccount): number {
            // 1 = taxable brokerage, 2 = non-roth, 3 = roth
            if (
                retirementAccountTaxTreatment[account.type] ===
                RetirementAccountTaxTreatment.TAXABLE
            ) {
                return 1;
            }
            if (
                retirementAccountTaxTreatment[account.type] ===
                RetirementAccountTaxTreatment.NON_ROTH
            ) {
                return 2;
            }
            if (
                retirementAccountTaxTreatment[account.type] ===
                RetirementAccountTaxTreatment.ROTH
            ) {
                return 3;
            }
            // fallback for unknown types
            return 99;
        }

        const typeOrderA = getTaxTreatmentOrder(a);
        const typeOrderB = getTaxTreatmentOrder(b);

        if (typeOrderA < typeOrderB) {
            return -1;
        }
        if (typeOrderA > typeOrderB) {
            return 1;
        }

        // If same type, keep existing logic: owner, then id
        if (a.owner < b.owner) {
            return -1;
        }
        if (a.owner > b.owner) {
            return 1;
        }
        if (a.id < b.id) {
            return -1;
        }
        if (a.id > b.id) {
            return 1;
        }
        return 0;
    }
}
